import React from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import type { SegmentRow, SegmentVehicleItem } from '../apiService/emailsGridApiService';
import type { VehicleListKind } from '../hooks/useSegmentInventories';
import { PreferencesContent } from './PreferencesContent';

export interface PreferencesModalProps {
  open: boolean;
  onClose: () => void;
  /** Rows from API (properties.rows) — show segment_name, new.count, used.count as returned. */
  segmentRows: SegmentRow[];
  loading: boolean;
  error: string | null;
  headerRow: string[];
  expandedCell: { segmentName: string; kind: VehicleListKind } | null;
  vehiclesForExpandedCell: SegmentVehicleItem[];
  vehiclesLoading: boolean;
  vehiclesError: string | null;
  /** Preferred VINs for current segment+kind (persisted; updated on manual select/deselect). */
  preferredVinsForExpandedCell: string[];
  /** VINs in preferred but not in segment-inventory (show as missing/cut, no select). */
  missingVinsForExpandedCell: string[];
  /** Call when user toggles a card — updates preferences state for future update API. */
  onTogglePreferredVin: (segmentName: string, kind: VehicleListKind, vin: string, selected: boolean) => void;
  onToggleExpanded: (segmentName: string, kind: VehicleListKind) => void;
  /** Save preferences (PUT). On success parent should close modal. */
  onSavePreferences: () => Promise<void>;
  saveLoading: boolean;
  /** When false, Save button is disabled (e.g. no preference_id from GET). */
  canSavePreferences: boolean;
}

export function PreferencesModal({
  open,
  onClose,
  segmentRows,
  headerRow,
  loading,
  error,
  expandedCell,
  vehiclesForExpandedCell,
  vehiclesLoading,
  vehiclesError,
  preferredVinsForExpandedCell,
  missingVinsForExpandedCell,
  onTogglePreferredVin,
  onToggleExpanded,
  onSavePreferences,
  saveLoading,
  canSavePreferences,
}: PreferencesModalProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}
      aria-labelledby="preferences-modal-title"
    >
      <DialogTitle id="preferences-modal-title" sx={{ flexShrink: 0 }}>
        Preferences
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minHeight: 0,
        }}
      >
        <PreferencesContent
          isOpen={open}
          segmentRows={segmentRows}
          headerRow={headerRow}
          loading={loading}
          error={error}
          expandedCell={expandedCell}
          vehiclesForExpandedCell={vehiclesForExpandedCell}
          vehiclesLoading={vehiclesLoading}
          vehiclesError={vehiclesError}
          preferredVinsForExpandedCell={preferredVinsForExpandedCell}
          missingVinsForExpandedCell={missingVinsForExpandedCell}
          onTogglePreferredVin={onTogglePreferredVin}
          onToggleExpanded={onToggleExpanded}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'flex-end', px: 3, pb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => onSavePreferences().then(() => onClose()).catch(() => {})}
          disabled={saveLoading || !canSavePreferences}
          startIcon={saveLoading ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {saveLoading ? 'Saving...' : 'Save Preferences'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
